/**
 * ApplicationActions — подвал карточки заявки: смена статуса и отказ.
 *
 * Переходы свободные, кнопка текущего статуса просто не рисуется:
 * решение можно пересмотреть, и «вернуть в работу» — штатный путь, а
 * не исправление ошибки.
 *
 * Отдельный случай — 409 `open_lead_exists`. Он приходит, когда
 * разобранный лид возвращают в работу, а по тому же номеру уже лежит
 * другая открытая заявка: держать две открытые по одному телефону
 * нельзя, их разберут порознь и позвонят человеку дважды. Поэтому
 * мало сказать «нельзя» — даём перейти к той заявке, иначе менеджер
 * останется искать её по номеру руками.
 */

import type {ApplicationStatus} from '@services/applications';
import {Button} from '@components/ui/Button';
import {extractErrorMessage, getErrorCode, getErrorString} from '@utils/httpError';

type Props = {
  status: ApplicationStatus;
  pending: boolean;
  error: unknown;
  onSet: (status: ApplicationStatus) => void;
  /** Перейти к заявке, которая мешает вернуть эту в работу */
  onOpenOther: (id: string) => void;
};

export const ApplicationActions = ({status, pending, error, onSet, onOpenOther}: Props) => {
  const conflictId =
    getErrorCode(error) === 'open_lead_exists' ? getErrorString(error, 'openApplicationId') : null;

  const handlePending = () => onSet('pending');
  const handleRejected = () => onSet('rejected');
  const handleReviewed = () => onSet('reviewed');

  return (
    <div className="border-t border-skr-line px-[22px] py-4">
      {error !== null && error !== undefined && (
        <div className="mb-3 rounded-[10px] border border-destructive/30 bg-destructive/[.07] px-3 py-2.5 text-[12.5px] leading-[1.45] text-destructive">
          {extractErrorMessage(error, 'Не удалось сменить статус')}
          {conflictId !== null && (
            <button
              onClick={() => onOpenOther(conflictId)}
              className="mt-1.5 block font-semibold underline underline-offset-2"
            >
              Открыть ту заявку
            </button>
          )}
        </div>
      )}

      <div className="flex gap-2.5">
        {status !== 'pending' && (
          <Button
            variant="outline"
            className="h-11 flex-1 rounded-[10px]"
            disabled={pending}
            onClick={handlePending}
          >
            Вернуть в работу
          </Button>
        )}
        {status !== 'rejected' && (
          <Button
            variant="outline"
            className="h-11 flex-1 rounded-[10px] text-destructive hover:text-destructive"
            disabled={pending}
            onClick={handleRejected}
          >
            Отклонить
          </Button>
        )}
        {status !== 'reviewed' && (
          <Button
            className="h-11 flex-1 rounded-[10px]"
            disabled={pending}
            onClick={handleReviewed}
          >
            Рассмотрена
          </Button>
        )}
      </div>
    </div>
  );
};
